import { useState } from 'react';
import { Button, Flex, Spinner, Text, TextArea } from '@radix-ui/themes';
import { Camera, Clipboard, Lock, Power, RotateCcw, Send } from 'lucide-react';
import type { Proc } from '@mac-remote/protocol';
import type { Shot } from './Screen';

interface Props {
  disabled: boolean;
  clipText: string | null;
  procs: Proc[] | null;
  webcam: Shot | null;
  pending: string | null;
  onVolume: (level: number) => void;
  onLock: () => void;
  onRestart: () => void;
  onShutdown: () => void;
  onClipGet: () => void;
  onClipSet: (text: string) => void;
  onPs: () => void;
  onKill: (pid: number, name: string) => void;
  onWebcam: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Flex direction="column" gap="2" style={{ borderBottom: '1px solid var(--gray-a4)', padding: '12px' }}>
      <Text size="1" weight="bold" style={{ color: 'var(--app-hint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Text>
      {children}
    </Flex>
  );
}

/**
 * Everything about the Mac's hardware and OS that a terminal can't reach: clipboard sync,
 * volume, screen lock, power, a camera peek, and the process list with a kill button.
 * Power actions are guarded with a confirm in App — this screen only signals intent.
 */
export function System({
  disabled, clipText, procs, webcam, pending,
  onVolume, onLock, onRestart, onShutdown, onClipGet, onClipSet, onPs, onKill, onWebcam,
}: Props) {
  const [vol, setVol] = useState(50);
  const [toMac, setToMac] = useState('');

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <Section title="Буфер обмена">
        <Flex gap="2" wrap="wrap">
          <Button size="1" variant="soft" color="gray" disabled={disabled} onClick={onClipGet}>
            <Clipboard size={13} /> Забрать с мака
          </Button>
        </Flex>
        {clipText !== null && (
          <TextArea readOnly value={clipText} rows={3} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        )}
        <TextArea
          placeholder="Текст → на буфер мака"
          value={toMac}
          onChange={(e) => setToMac(e.target.value)}
          rows={2}
          disabled={disabled}
        />
        <Button size="1" variant="soft" color="jade" disabled={disabled || !toMac} onClick={() => { onClipSet(toMac); setToMac(''); }}>
          <Send size={13} /> Отправить на мак
        </Button>
      </Section>

      <Section title="Громкость">
        <Flex align="center" gap="3">
          <input
            type="range"
            min={0}
            max={100}
            value={vol}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => setVol(Number(e.target.value))}
            onPointerUp={() => onVolume(vol)}
          />
          <Text size="2" style={{ width: 36, textAlign: 'right' }}>{vol}%</Text>
        </Flex>
      </Section>

      <Section title="Экран и питание">
        <Flex gap="2" wrap="wrap">
          <Button size="1" variant="soft" color="gray" disabled={disabled} onClick={onLock}>
            <Lock size={13} /> Заблокировать
          </Button>
          <Button size="1" variant="soft" color="amber" disabled={disabled} onClick={onRestart}>
            <RotateCcw size={13} /> Перезагрузить
          </Button>
          <Button size="1" variant="soft" color="red" disabled={disabled} onClick={onShutdown}>
            <Power size={13} /> Выключить
          </Button>
        </Flex>
      </Section>

      <Section title="Камера">
        <Button size="1" variant="soft" color="gray" disabled={disabled} onClick={onWebcam}>
          <Camera size={13} /> {pending === 'webcam' ? <Spinner size="1" /> : 'Снять кадр'}
        </Button>
        {webcam && (
          <img
            src={`data:image/jpeg;base64,${webcam.b64}`}
            alt="Камера"
            style={{ maxWidth: '100%', borderRadius: 6, marginTop: 4 }}
          />
        )}
      </Section>

      <Section title="Процессы">
        <Button size="1" variant="soft" color="gray" disabled={disabled} onClick={onPs}>
          {pending === 'ps' ? <Spinner size="1" /> : 'Обновить список'}
        </Button>
        {procs && (
          <Flex direction="column" gap="1" mt="1">
            <Flex px="1" gap="2" style={{ color: 'var(--app-hint)' }}>
              <Text size="1" style={{ flex: 1 }}>Процесс</Text>
              <Text size="1" style={{ width: 48, textAlign: 'right' }}>CPU</Text>
              <Text size="1" style={{ width: 48, textAlign: 'right' }}>MEM</Text>
              <Text size="1" style={{ width: 40 }} />
            </Flex>
            {procs.map((p) => (
              <Flex key={p.pid} align="center" gap="2" px="1" py="1" style={{ borderTop: '1px solid var(--gray-a3)' }}>
                <Text size="1" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Text>
                <Text size="1" style={{ width: 48, textAlign: 'right', color: p.cpu > 50 ? 'var(--red-11)' : undefined }}>{p.cpu.toFixed(0)}%</Text>
                <Text size="1" style={{ width: 48, textAlign: 'right' }}>{p.mem.toFixed(1)}%</Text>
                <Button size="1" variant="ghost" color="red" style={{ width: 40 }} disabled={disabled} onClick={() => onKill(p.pid, p.name)}>
                  kill
                </Button>
              </Flex>
            ))}
          </Flex>
        )}
      </Section>
    </div>
  );
}
